import clsx from 'clsx';
import type { CSSProperties } from 'react';

import styles from './KeyStats.module.scss';

/** One inline fragment of a stat value (e.g. primary figure vs. delta). */
export type ValueSegment = {
  /** Display text for this segment. */
  text: string;
  /**
   * Optional sentiment color for secondary segments.
   * `'bullish'` and `'bearish'` apply viz token colors; omit for neutral text.
   */
  colorVariant?: 'bullish' | 'bearish';
};

/** A single labeled stat tile in the grid. */
export type KeyStatItem = {
  /** Short descriptor shown above the value (e.g. "Revenue", "EPS"). */
  label: string;
  /**
   * Value rendered as one or more inline segments.
   * First segment uses body-base typography; colored segments use body-xs.
   */
  value: ValueSegment[];
};

export interface KeyStatsProps {
  /** Stat tiles to render. Order is preserved left-to-right, top-to-bottom. */
  items: KeyStatItem[];
  /**
   * Maximum columns per row before wrapping. Defaults to `4`.
   * Fewer items than this expand equally; overflow rows keep the same column cap.
   */
  itemsPerRow?: number;
}

type KeyStatsStyle = CSSProperties & {
  '--key-stats-items-per-row': number;
};

/**
 * Responsive grid of labeled metric tiles for financial or KPI summaries.
 *
 * Each tile shows a muted label and a value built from inline `ValueSegment`s.
 * Use multiple segments to pair a primary figure with a colored delta or qualifier
 * (e.g. `"$12.4B"` + `"+3.2%"` with `colorVariant: 'bullish'`).
 *
 * Layout is CSS-grid based: `itemsPerRow` sets the column cap; tiles wrap
 * automatically when there are more items than columns.
 */
export const KeyStats = ({ items, itemsPerRow = 4 }: KeyStatsProps) => {
  const rootStyle: KeyStatsStyle = {
    '--key-stats-items-per-row': itemsPerRow,
  };

  return (
    <div className={styles.root} style={rootStyle}>
      {items.map((item, i) => (
        <div key={i} className={styles.tileCard}>
          <span className={styles.tileLabel}>{item.label}</span>
          <span className={styles.tileValue}>
            {item.value.map((segment, i) => (
              <span
                key={i}
                className={clsx({
                  [styles.bullish]: segment.colorVariant === 'bullish',
                  [styles.bearish]: segment.colorVariant === 'bearish',
                })}
              >
                {segment.text}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
};
